import React, { useRef, useState } from 'react';
import { studioApi } from '../api/studioClient';
import { Alert, Box, Button, Container, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../i18n/inlineTranslations';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { KundlistarChartRow, parseKundlistarChartFile } from '../utils/kundlistarChartFile';

type ImportStatus = 'pending' | 'saved' | 'duplicate' | 'error';

export default function KundlistarChartImport() {
  const [rows, setRows] = useState<KundlistarChartRow[]>([]);
  const [statuses, setStatuses] = useState<Record<string, ImportStatus>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const running = useRef(false);
  const stop = useRef(false);
  const { ayanamshaMode, nodeMode, chartStyle } = useUserPreferences();
  const { i18n } = useTranslation();
  const tr = (english: string, hindi: string) => translateInline(i18n.resolvedLanguage || i18n.language, english, hindi);

  async function chooseFile(file?: File) {
    if (!file) return;
    setRows([]); setStatuses({}); setErrors([]); setMessage(''); setFileName(file.name);
    if (file.size > 15 * 1024 * 1024) { setErrors(['File is too large. Maximum size is 15 MB.']); return; }
    try {
      const parsed = parseKundlistarChartFile(await file.text());
      setRows(parsed.rows); setErrors(parsed.errors);
    } catch { setErrors(['Could not read this file. Choose a Kundlistar chart export .txt file.']); }
  }

  async function importRows() {
    if (running.current) return;
    running.current = true; stop.current = false; setBusy(true); setMessage('');
    const pending = rows.filter(row => !['saved', 'duplicate'].includes(statuses[row.id]));
    try {
      for (let index = 0; index < pending.length; index++) {
        if (stop.current) break;
        const row = pending[index];
        setProgress(index + 1);
        const [hour, minute, second] = row.time.split(':').map(Number);
        try {
          const response = await studioApi.post('/charts/calculate', {
            importChart: true, saveChart: true, createdFrom: 'web',
            name: row.name, gender: row.gender, dateOfBirth: row.date,
            timeOfBirth: { hour, minute, second },
            placeOfBirth: { city: row.city, state: row.state, country: row.country,
              latitude: row.latitude, longitude: row.longitude, timezone: row.offsetMinutes,
              timezoneSource: 'source', isCustomLocation: true },
            ayanamshaMode, chartSettings: { nodeMode, chartStyle, language: 'English' }
          });
          const result = response.data;
          if (!result.data?.saved || !result.data?.chartId) throw new Error(result.data?.saveError || 'Chart was not saved.');
          setStatuses(current => ({ ...current, [row.id]: result.duplicate ? 'duplicate' : 'saved' }));
        } catch (error: any) {
          setStatuses(current => ({ ...current, [row.id]: 'error' }));
          setMessage(error.response?.data?.message || error.message || 'Import stopped. Retry to continue.');
          break;
        }
      }
    } finally { running.current = false; setBusy(false); }
  }

  const completed = rows.filter(row => ['saved', 'duplicate'].includes(statuses[row.id])).length;
  return <Container maxWidth="md" sx={{ py: 4 }}>
    <Button component={Link} to="/settings">← {tr('Settings', 'सेटिंग्स')}</Button>
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>{tr('Import Kundlistar chart file', 'Kundlistar चार्ट फ़ाइल इम्पोर्ट करें')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{tr('Choose the .txt file from Export all charts. Review the birth details, then import the charts into your account.', 'सभी चार्ट एक्सपोर्ट करें से मिली .txt फ़ाइल चुनें। जन्म विवरण जाँचें, फिर चार्ट अपने खाते में इम्पोर्ट करें।')}</Typography>
      <Button component="label" variant="outlined" disabled={busy}>{tr('Choose chart file', 'चार्ट फ़ाइल चुनें')}<input hidden type="file" accept=".txt,text/plain" onChange={event => { void chooseFile(event.target.files?.[0]); event.target.value = ''; }} /></Button>
      {fileName && <Typography sx={{ mt: 1 }}>{fileName}</Typography>}
      {errors.length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{errors.length} row(s) could not be imported.<Box component="ul" sx={{ mb: 0 }}>{errors.slice(0, 20).map((error, index) => <li key={index}>{error}</li>)}</Box></Alert>}
      {rows.length > 0 && <>
        <Typography variant="h6" sx={{ mt: 3 }}>{tr('Review charts', 'चार्ट जाँचें')} ({rows.length})</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>{tr('Birth details and exported coordinates are retained. Only birth charts are supported. Your account’s chart save limit applies.', 'जन्म विवरण और एक्सपोर्ट किए गए निर्देशांक सुरक्षित रहते हैं। केवल जन्म चार्ट इम्पोर्ट किए जा सकते हैं। खाते की चार्ट सीमा लागू होती है।')}</Typography>
        <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {rows.map(row => <Box key={row.id} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography fontWeight={600}>{row.name} · {row.date} {row.time}</Typography>
            <Typography variant="body2" color="text.secondary">{row.city}, {row.state}, {row.country} · {row.latitude}, {row.longitude} · UTC {row.offsetMinutes / 60} · {statuses[row.id] || 'Ready'}</Typography>
          </Box>)}
        </Box>
        {busy && <Box sx={{ mt: 2 }}><Typography>{tr('Importing', 'इम्पोर्ट हो रहे हैं')} {progress} / {rows.length}</Typography><LinearProgress /></Box>}
        {message && <Alert severity="info" sx={{ mt: 2 }}>{message}</Alert>}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={busy || completed === rows.length} onClick={importRows}>{tr('Import', 'इम्पोर्ट करें')} {rows.length - completed} {tr('charts', 'चार्ट')}</Button>
          {busy && <Button onClick={() => { stop.current = true; }}>{tr('Stop after current chart', 'मौजूदा चार्ट के बाद रोकें')}</Button>}
          {completed > 0 && <Button component={Link} to="/dashboard">{tr('View saved charts', 'सेव किए गए चार्ट देखें')}</Button>}
        </Stack>
      </>}
    </Paper>
  </Container>;
}
